import React, { useRef, useImperativeHandle, forwardRef } from 'react';

// iter-39: forwardRef so the parent (Step07/Step17) can imperatively
// clear all boxes on a rejected-code reset event. Paste support added
// — pasting a 6-digit string into ANY box splits across all boxes and
// fires onComplete once the code reaches `length` digits.
const CodeInput = forwardRef(function CodeInput({ length = 6, onComplete }, ref) {
  const inputs = useRef([]);

  useImperativeHandle(ref, () => ({
    clear: () => {
      inputs.current.forEach((el) => { if (el) el.value = ''; });
      inputs.current[0]?.focus();
    },
  }), []);

  const fireOnComplete = () => {
    const code = inputs.current.map((el) => el?.value || '').join('');
    if (code.length === length) onComplete(code);
  };

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    if (value && index < length - 1) {
      inputs.current[index + 1]?.focus();
    }
    fireOnComplete();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  // iter-39: paste handler — accept the full code in one shot. Strips
  // non-digits + spaces (operators commonly paste with a leading space
  // or hyphen). Pads/truncates to length, distributes one digit per
  // box, focuses the last filled box, fires onComplete if we got all
  // `length` digits.
  const handlePaste = (e) => {
    const raw = (e.clipboardData || window.clipboardData)?.getData('text') || '';
    const digits = raw.replace(/\D+/g, '').slice(0, length);
    if (!digits) return;
    e.preventDefault();
    inputs.current.forEach((el, i) => {
      if (!el) return;
      el.value = digits[i] || '';
    });
    const lastFilled = Math.min(digits.length, length) - 1;
    inputs.current[Math.max(0, Math.min(lastFilled, length - 1))]?.focus();
    fireOnComplete();
  };

  return (
    <div className="code-input">
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          autoComplete="one-time-code"
          className="code-digit"
        />
      ))}
    </div>
  );
});

export default CodeInput;
